import { FC, useState } from "react";
import { Button } from "react-bootstrap";
import PriceSheetColumnHeaders from "./PriceSheetColumnHeaders";
import PriceSheetLineItem, { LineItemRow } from "./PriceSheetLineItem";
import { type JourneyStepOption } from "./JourneyStepSelect";

export interface PathAccordionEntry {
  id: number;
  name: string;
  /** Display name of the population this group prices. */
  populationSegmentName?: string | null;
}

interface Props {
  paths: PathAccordionEntry[];
  rows: LineItemRow[];
  stepOptions: JourneyStepOption[];
  organizationId: number;
  contractId?: number | null;
  pricesheetId?: number | null;
  onAddPath: () => void;
  onRenamePath: (pathId: number | null) => void;
  onDeletePath: (pathId: number | null) => void;
  onAddItem: (pathId: number | null) => void;
  onChangeItem: (id: number, patch: Partial<LineItemRow>) => void;
  onDeleteItem: (id: number) => void;
  onDimChange?: (
    itemId: number,
    journeyStepDimensionId: number | null,
    journeyStepDimensionName: string | null,
  ) => void;
  /** When false, hides the "+ Add Group" button. Defaults to true. */
  allowAddPath?: boolean;
  /** Rows that share a (step, dimension) cell with another row in their group. */
  duplicateItemIds?: ReadonlySet<number>;
}

/** Display order within a group. Sequence is typed now, not dragged, so the row
 *  has to move the moment the number is committed — the drafts array is no
 *  longer reordered for us (MPN-1589). Ties break on id so the order is stable
 *  when two steps share a number; nothing forbids that. */
function groupOptionsFrom(paths: PathAccordionEntry[]): { id: number; name: string }[] {
  return paths.map((p) => ({ id: p.id, name: p.name }));
}

/** Rows that share a (group, step, dimension) cell with another row. The server
 *  refuses the second one (uq_pricesheetitem_group_step_dimension), so both are
 *  flagged and Save is held until the operator moves one (MPN-1713). */
export function duplicateCellIds(rows: readonly LineItemRow[]): Set<number> {
  const firstOnCell = new Map<string, number>();
  const dupes = new Set<number>();
  for (const r of rows) {
    if (r.journey_step_id <= 0) continue;
    const cell = `${r.journey_path_id}:${r.journey_step_id}:${r.journey_step_dimension_id ?? "default"}`;
    const first = firstOnCell.get(cell);
    if (first === undefined) firstOnCell.set(cell, r.id);
    else dupes.add(first).add(r.id);
  }
  return dupes;
}

function bySequence(items: LineItemRow[]): LineItemRow[] {
  return [...items].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0) || a.id - b.id);
}

const PathsAccordion: FC<Props> = ({
  paths,
  rows,
  stepOptions,
  organizationId,
  contractId,
  pricesheetId,
  onAddPath,
  onRenamePath,
  onDeletePath,
  onAddItem,
  onChangeItem,
  onDeleteItem,
  onDimChange,
  allowAddPath = true,
  duplicateItemIds,
}) => {
  const sections = paths.map((p) => ({
    key: String(p.id),
    pathId: p.id as number | null,
    name: p.name || `Group #${p.id}`,
    populationSegmentName: p.populationSegmentName ?? null,
    renameable: true,
    deletable: true,
    items: bySequence(rows.filter((r) => r.journey_path_id === p.id)),
  }));
  const unassigned = bySequence(rows.filter((r) => !r.journey_path_id));
  const groupOptions = groupOptionsFrom(paths);
  if (unassigned.length > 0) {
    sections.push({
      key: "unassigned",
      pathId: null,
      name: "Unassigned",
      populationSegmentName: null,
      renameable: true,
      deletable: true,
      items: unassigned,
    });
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 fw-bold">Groups</h6>
        {allowAddPath && (
          <Button variant="outline-primary" size="sm" onClick={onAddPath}>
            + Add Group
          </Button>
        )}
      </div>

      {sections.map((s) => (
        <PathSection
          key={s.key}
          pathId={s.pathId}
          name={s.name}
          populationSegmentName={s.populationSegmentName}
          renameable={s.renameable}
          deletable={s.deletable}
          items={s.items}
          stepOptions={stepOptions}
          organizationId={organizationId}
          contractId={contractId}
          pricesheetId={pricesheetId}
          onRename={() => onRenamePath(s.pathId)}
          onDelete={() => onDeletePath(s.pathId)}
          onAddItem={() => onAddItem(s.pathId)}
          onChangeItem={onChangeItem}
          onDeleteItem={onDeleteItem}
          onDimChange={onDimChange}
          groupOptions={groupOptions}
          duplicateItemIds={duplicateItemIds}
        />
      ))}
    </div>
  );
};

interface PathSectionProps {
  pathId: number | null;
  name: string;
  populationSegmentName?: string | null;
  renameable: boolean;
  deletable: boolean;
  items: LineItemRow[];
  stepOptions: JourneyStepOption[];
  organizationId: number;
  contractId?: number | null;
  pricesheetId?: number | null;
  onRename: () => void;
  onDelete: () => void;
  onAddItem: () => void;
  onChangeItem: (id: number, patch: Partial<LineItemRow>) => void;
  onDeleteItem: (id: number) => void;
  onDimChange?: (
    itemId: number,
    journeyStepDimensionId: number | null,
    journeyStepDimensionName: string | null,
  ) => void;
  /** Every group on the sheet — a step can be moved to any of them. */
  groupOptions: { id: number; name: string }[];
  duplicateItemIds?: ReadonlySet<number>;
}

const PathSection: FC<PathSectionProps> = ({
  pathId,
  name,
  populationSegmentName,
  renameable,
  deletable,
  items,
  stepOptions,
  organizationId,
  contractId,
  pricesheetId,
  onRename,
  onDelete,
  onAddItem,
  onChangeItem,
  onDeleteItem,
  onDimChange,
  groupOptions,
  duplicateItemIds,
}) => {
  const [expanded, setExpanded] = useState(true);

  const handleStepChange = (itemId: number, _index: number, stepId: string) => {
    const selected = stepOptions.find((s) => s.id === Number(stepId));
    if (!selected) return;
    const current = items.find((r) => r.id === itemId);
    const patch: Partial<LineItemRow> = { journey_step_id: selected.id };
    if (!current?.name) patch.name = selected.name;
    onChangeItem(itemId, patch);
  };
  const handleNameChange = (itemId: number, val: string) => onChangeItem(itemId, { name: val });
  const handleConversionRuleChange = (itemId: number, ruleText: string) =>
    onChangeItem(itemId, { conversion_rule_text: ruleText });
  const handleFieldChange = (
    index: number,
    field: "step_price" | "estimated_roi" | "unit_price",
    raw: string,
  ) => {
    const value = raw === "" ? 0 : parseFloat(raw);
    if (isNaN(value)) return;
    const item = items[index];
    onChangeItem(item.id, { [field]: value });
  };

  return (
    <div
      className="mb-3 bg-white"
      style={{
        borderRadius: 10,
        boxShadow: "0 1px 2px rgba(15,23,42,0.06), 0 4px 12px rgba(15,23,42,0.04)",
      }}
    >
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ padding: "14px 20px" }}
      >
        <div className="d-flex align-items-center gap-2 flex-grow-1 me-2">
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-none text-muted"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Collapse" : "Expand"}
            style={{ lineHeight: 1 }}
          >
            <i className={`bi ${expanded ? "bi-chevron-down" : "bi-chevron-right"}`} />
          </Button>
          <span className="fw-semibold" style={{ fontSize: "1rem", color: "#1c2030" }}>
            {name}
          </span>
          <span className="text-muted small">
            · {items.length} {items.length === 1 ? "step" : "steps"}
          </span>
          {pathId !== null && (
            <span
              className="badge rounded-pill"
              style={{ background: "#eef2ff", color: "#4f46e5", fontWeight: 500 }}
              title="The population this group's steps price"
            >
              {populationSegmentName ?? "—"}
            </span>
          )}
        </div>
        {(renameable || deletable) && (
          <div className="d-flex align-items-center">
            {renameable && (
              <HoverIconButton icon="bi-pencil" hoverColor="var(--bs-primary)" onClick={onRename} title="Rename group" />
            )}
            <div style={{ width: 42 }} className="d-flex justify-content-center">
              {deletable && (
                <HoverIconButton icon="bi-x-lg" hoverColor="var(--bs-danger)" onClick={onDelete} title="Remove group" />
              )}
            </div>
          </div>
        )}
      </div>

      {expanded && (
        <div style={{ padding: "0 20px 20px" }}>
          {items.length > 0 ? (
            <>
              <PriceSheetColumnHeaders />
              {items.map((item, index) => (
                <PriceSheetLineItem
                  key={item.id}
                  item={item}
                  index={index}
                  stepOptions={stepOptions}
                  organizationId={organizationId}
                  contractId={contractId}
                  pricesheetId={pricesheetId}
                  onStepChange={handleStepChange}
                  onNameChange={handleNameChange}
                  onConversionRuleChange={handleConversionRuleChange}
                  onFieldChange={handleFieldChange}
                  onAttrChange={(id, patch) => onChangeItem(id, patch)}
                  onDelete={onDeleteItem}
                  onDimChange={onDimChange}
                  groupOptions={groupOptions}
                  isDuplicate={duplicateItemIds?.has(item.id) ?? false}
                />
              ))}
            </>
          ) : (
            <div className="text-muted small fst-italic mb-2">No line items yet.</div>
          )}
          <div className="mt-2">
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none"
              onClick={onAddItem}
              style={{ fontWeight: 500 }}
            >
              + Add line item{pathId === null ? "" : " to this group"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

interface HoverIconButtonProps {
  icon: string;
  hoverColor: string;
  onClick: () => void;
  title: string;
}

export const HoverIconButton: FC<HoverIconButtonProps> = ({ icon, hoverColor, onClick, title }) => {
  const [hover, setHover] = useState(false);
  return (
    <Button
      variant="link"
      size="sm"
      className="p-1"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={title}
      style={{
        textDecoration: "none",
        lineHeight: 1,
        color: hover ? hoverColor : "var(--bs-secondary-color, #6c757d)",
        transform: hover ? "scale(1.2)" : "scale(1)",
        transition: "transform 150ms ease, color 150ms ease",
      }}
    >
      <i className={`bi ${icon}`} />
    </Button>
  );
};

export default PathsAccordion;
